import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faComments, faCheck } from "@fortawesome/free-solid-svg-icons";
import postApi from "../../../api/postApi";
import { exportCommentsCsv } from "./helpers";
import useComments from "./hooks/use-comments";
import CommentStats from "./components/comment-stats";
import CommentFilterBar from "./components/comment-filter-bar";
import CommentThread from "./components/comment-thread";
import BannedWordsModal from "./components/banned-words-modal";
import CommentRateLimitModal from "./components/comment-rate-limit-modal";
import { buildCommentColumns } from "./components/comment-columns";

function CommentList() {
    const c = useComments();
    const [submitting, setSubmitting] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [bannedWordsOpen, setBannedWordsOpen] = useState(false);
    const [rateLimitOpen, setRateLimitOpen] = useState(false);

    // Đổi trang/bộ lọc -> bỏ chọn để tránh xóa nhầm dòng ngoài tầm nhìn
    useEffect(() => {
        setSelectedKeys([]);
    }, [c.page, c.q, c.postId, c.unanswered, c.status, c.dateRange]);

    // Duyệt bình luận chờ duyệt (1 dòng hoặc hàng loạt)
    const handleApprove = async (commentIds) => {
        try {
            await postApi.approveComments(commentIds);
            message.success("Đã duyệt bình luận");
            setSelectedKeys([]);
            c.refetch();
        } catch {
            message.error("Không thể duyệt bình luận");
        }
    };

    const handleApproveReply = async (replyId) => {
        try {
            await postApi.approveReplies([replyId]);
            message.success("Đã duyệt trả lời");
            c.refetch();
        } catch {
            message.error("Không thể duyệt trả lời");
        }
    };

    // Gửi trả lời (dùng chung /new_reply -> tự thông báo chủ bình luận)
    const handleReply = async (commentId, text) => {
        try {
            setSubmitting(true);
            await postApi.replyComment(commentId, text);
            message.success("Đã gửi trả lời");
            c.refetch();
        } catch {
            message.error("Không thể gửi trả lời");
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDelete = (label, fn) =>
        Modal.confirm({
            title: "Xác nhận xóa",
            content: `${label} sẽ bị xóa vĩnh viễn. Bạn có chắc không?`,
            okText: "Xóa",
            cancelText: "Hủy",
            okType: "danger",
            onOk: async () => {
                try {
                    await fn();
                    message.success("Đã xóa");
                    setSelectedKeys([]);
                    c.refetch();
                } catch {
                    message.error("Không thể xóa");
                }
            },
        });

    // Mở/đóng hội thoại của 1 dòng (nút "Trả lời" sẽ bung dòng đó)
    const toggleExpand = (commentId) =>
        setExpandedKeys((keys) =>
            keys.includes(commentId) ? keys.filter((k) => k !== commentId) : [...keys, commentId]
        );

    const columns = buildCommentColumns({
        onReplyToggle: toggleExpand,
        onDelete: (r) =>
            confirmDelete("Bình luận (và mọi trả lời)", () => postApi.deleteComment(r.commentId)),
        onApprove: (r) => handleApprove([r.commentId]),
    });

    // Trong các dòng đang chọn, dòng nào còn chờ duyệt (để bulk approve đúng đối tượng)
    const selectedPendingIds = c.comments
        .filter((cm) => selectedKeys.includes(cm.commentId) && cm.status === 0)
        .map((cm) => cm.commentId);

    return (
        <div className="flex-1 p-3 md:p-6 overflow-auto">
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2 mb-5">
                <FontAwesomeIcon icon={faComments} className="text-blue-400" /> Quản lý Bình luận
            </h1>

            <CommentStats
                total={c.total}
                pendingTotal={c.pendingTotal}
                unansweredTotal={c.unansweredTotal}
                shown={c.comments.length}
            />

            <CommentFilterBar
                q={c.q}
                postId={c.postId}
                unanswered={c.unanswered}
                status={c.status}
                pendingTotal={c.pendingTotal}
                dateRange={c.dateRange}
                loading={c.loading}
                onSearch={c.setQ}
                onPostId={c.setPostId}
                onToggleUnanswered={c.setUnanswered}
                onStatusChange={c.setStatus}
                onDateChange={c.setDateRange}
                onExport={() => exportCommentsCsv(c.comments)}
                onRefresh={c.refetch}
                onOpenBannedWords={() => setBannedWordsOpen(true)}
                onOpenRateLimit={() => setRateLimitOpen(true)}
            />

            <BannedWordsModal open={bannedWordsOpen} onClose={() => setBannedWordsOpen(false)} />
            <CommentRateLimitModal open={rateLimitOpen} onClose={() => setRateLimitOpen(false)} />

            {selectedKeys.length > 0 && (
                <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm text-content-3">Đã chọn {selectedKeys.length}</span>
                    {selectedPendingIds.length > 0 && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<FontAwesomeIcon icon={faCheck} />}
                            onClick={() => handleApprove(selectedPendingIds)}
                        >
                            Duyệt {selectedPendingIds.length} chờ duyệt
                        </Button>
                    )}
                    <Button
                        danger
                        size="small"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() =>
                            confirmDelete(`${selectedKeys.length} bình luận (và mọi trả lời)`, () =>
                                postApi.bulkDeleteComments(selectedKeys)
                            )
                        }
                    >
                        Xóa đã chọn
                    </Button>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={c.comments}
                loading={c.loading}
                rowKey="commentId"
                size="middle"
                scroll={{ x: "max-content" }}
                rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                expandable={{
                    expandedRowKeys: expandedKeys,
                    onExpandedRowsChange: (keys) => setExpandedKeys(keys),
                    expandedRowRender: (record) => (
                        <CommentThread
                            record={record}
                            submitting={submitting}
                            onReply={handleReply}
                            onDeleteReply={(id) =>
                                confirmDelete("Trả lời", () => postApi.deleteReply(id))
                            }
                            onApproveReply={handleApproveReply}
                        />
                    ),
                }}
                pagination={{
                    current: c.page,
                    pageSize: c.pageSize,
                    total: c.total,
                    showSizeChanger: false,
                    onChange: (p) => c.setPage(p),
                    showTotal: (t) => `${t} bình luận`,
                }}
            />
        </div>
    );
}

export default CommentList;
